import { BACKEND_URL } from '@/utils/apiConfig';
/**
 * Push Notification utilities for George Dives PWA
 */

const API = `${BACKEND_URL}/api`;

// Check if push notifications are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

// Check if notification permission is granted
export const isNotificationPermitted = () => {
  return Notification.permission === 'granted';
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isPushSupported()) {
    console.log('Push notifications not supported');
    return false;
  }
  
  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

// Register service worker
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service workers not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

// Subscribe to push notifications
export const subscribeToPush = async (token) => {
  if (!isPushSupported()) return null;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Get existing subscription or create new one
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Get VAPID public key from server
      const response = await fetch(`${API}/push/vapid-public-key`);
      if (!response.ok) {
        console.log('Push notifications not configured on server');
        return null;
      }
      
      const { publicKey } = await response.json();
      
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
    }
    
    // Send subscription to server
    await fetch(`${API}/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(subscription)
    });
    
    console.log('Push subscription saved');
    return subscription;
  } catch (error) {
    console.error('Failed to subscribe to push:', error);
    return null;
  }
};

// Endpoint of this device's current push subscription (null if none / unsupported)
export const getLocalPushEndpoint = async () => {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    return sub ? sub.endpoint : null;
  } catch (e) {
    return null;
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async (token) => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      
      // Remove subscription from server
      await fetch(`${API}/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });
    }
    
    console.log('Unsubscribed from push notifications');
    return true;
  } catch (error) {
    console.error('Failed to unsubscribe:', error);
    return false;
  }
};

// Badge count tracker for combining multiple sources
const badgeCounts = {
  notifications: 0,
  chat: 0
};

// Update badge count from a specific source
export const updateBadgeSource = (source, count) => {
  badgeCounts[source] = count || 0;
  const total = badgeCounts.notifications + badgeCounts.chat;
  setBadgeCount(total);
};

// Set PWA badge count (iOS requires a numeric value; flag-only badges are ignored)
export const setBadgeCount = (count) => {
  const n = Number(count) || 0;
  if ('setAppBadge' in navigator) {
    const p = n > 0 ? navigator.setAppBadge(n) : navigator.clearAppBadge();
    if (p && p.catch) p.catch(() => {});
  }
  
  // Also send to service worker for fallback
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'SET_BADGE',
      count: n
    });
  }
};

// Clear PWA badge
export const clearBadge = () => {
  badgeCounts.notifications = 0;
  badgeCounts.chat = 0;
  setBadgeCount(0);
};

// Show a local notification (for testing or immediate feedback)
export const showLocalNotification = (title, options = {}) => {
  if (!isNotificationPermitted()) {
    console.log('Notification permission not granted');
    return;
  }
  
  const defaultOptions = {
    icon: '/android-chrome-192x192.png',
    badge: '/favicon-32x32.png',
    vibrate: [100, 50, 100]
  };
  
  return new Notification(title, { ...defaultOptions, ...options });
};

// Helper: Convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default {
  isPushSupported,
  isNotificationPermitted,
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
  setBadgeCount,
  updateBadgeSource,
  clearBadge,
  showLocalNotification
};
